import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import type { FinancialChartDatum } from "../../types/financialUi"
import {
  FINANCIAL_CHART_RESIZE_DEBOUNCE_MS,
  hasFinancialChartData,
  sanitizeFinancialSeries,
} from "../../utils/financialChartUtils"
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion"
import { FinancialEmptyState } from "../FinancialEmptyState"

interface FinancialHorizontalBarChartProps {
  data: readonly FinancialChartDatum[]
  ariaLabel: string
  emptyTitle: string
  emptyDescription?: string
  formatValue: (value: number) => string
  formatAxisValue?: (value: number) => string
  valueLabel?: string
}

export const FinancialHorizontalBarChart: React.FC<FinancialHorizontalBarChartProps> = ({
  data,
  ariaLabel,
  emptyTitle,
  emptyDescription,
  formatValue,
  formatAxisValue = formatValue,
  valueLabel,
}) => {
  const prefersReducedMotion = usePrefersReducedMotion()
  const chartData = sanitizeFinancialSeries(data)
  const chartHeight = Math.min(520, Math.max(260, chartData.length * 46 + 70))

  if (!hasFinancialChartData(chartData)) {
    return <FinancialEmptyState title={emptyTitle} description={emptyDescription} />
  }

  return (
    <div className="w-full min-w-0" style={{ height: chartHeight }} role="img" aria-label={ariaLabel}>
      <ResponsiveContainer width="100%" height="100%" debounce={FINANCIAL_CHART_RESIZE_DEBOUNCE_MS}>
        <BarChart data={chartData} layout="vertical" margin={{ top: 8, right: 28, left: 12, bottom: 4 }} accessibilityLayer>
          <defs>
            <linearGradient id="financialBarGradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#fb923c" />
              <stop offset="55%" stopColor="#f97316" />
              <stop offset="100%" stopColor="#ea580c" />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 5" horizontal={false} />
          <XAxis
            type="number"
            tickFormatter={(value: number) => formatAxisValue(value)}
            tick={{ fill: "#64748b", fontSize: 12, fontWeight: 600 }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="label"
            width={132}
            tick={{ fill: "#334155", fontSize: 12, fontWeight: 700 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(value: string) => (value.length > 21 ? `${value.slice(0, 20)}…` : value)}
          />
          <Tooltip
            formatter={(value) => [formatValue(Number(value ?? 0)), valueLabel]}
            cursor={{ fill: "#fff7ed" }}
            contentStyle={{ borderRadius: 12, borderColor: "#e2e8f0", boxShadow: "0 10px 30px rgba(15,23,42,.10)" }}
          />
          <Bar
            dataKey="value"
            name={valueLabel}
            radius={[0, 10, 10, 0]}
            fill="url(#financialBarGradient)"
            barSize={24}
            isAnimationActive={!prefersReducedMotion}
            animationDuration={900}
            animationEasing="ease-out"
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
